import React, { useEffect, useState } from 'react';
import { useCallbackPrompt } from '../_reusable/blocker/UsePromptComponent.ts';
import Button from 'shared/components/button/Button';
import Input from 'shared/components/input/Input';
import Switch from 'shared/components/switch/Switch';
import {
  uploadFileIcon,
  downloadFileIcon,
  disabledDownloadFile,
  addButtonIcon,
} from '../../assets/index';
import './BreakPatterns.css';
import useStore from 'store/AccountStore';
import ImportFileModalComponent from './modals/ImportFileModalComponent';
import BreakPatternForms from './BreakPatternForms';
import { saveAs } from 'file-saver';
import XLSX from 'sheetjs-style';
import useNotification from 'store/NotificationStore';
import {
  GetBreakChannelTypeAPI,
  SaveBreakChannelTemplateAPI,
  ExportBreakChannelTypeAPI,
  GetBreakChannelEventListAPI,
} from 'api';
import OpTimeInput from 'op2mise-react-widgets/OpTimeInput/OpTimeInput.jsx';
import { NumericTextbox, DatePicker } from 'op2mise-react-widgets/index.js';
import { Constants, FORM_TYPE } from './constants.js';
import ConfirmLeaveModal from 'shared/components/confirm-leave-modal/ConfirmLeaveModal.jsx';
import moment from 'moment';
import OpSelect from 'op2mise-react-widgets/OpSelect/OpSelect.jsx';
import BreakPatternEditorTable from './components/BreakPatternEditorTable.jsx';
import BreakPatternErrorMessage from './components/BreakPatternErrorMessage.jsx';
import { validateBreakPattern } from './breakPattern.helpers.js';
import BreakTypeEditor from './modals/break-type-editor/BreakTypeEditor.jsx';

function BreakPatternEditor({ mode, setMode, onClose, onImport, selectedRow, channelInfo }) {
  const { user } = useStore((state) => state);
  const { success } = useNotification((state) => state);

  const [isDirtyForm, setDirtyForm] = React.useState(false);
  const [showConfirmLeaveModal, setShowConfirmLeaveModal] = React.useState(false);
  const { sampleUploadFile, importModalComponents } = BreakPatternForms;
  const [uploadState, setUploadState] = React.useState({
    modalComponent: importModalComponents.import,
    errorMessage: '',
    resultList: [],
    show: false,
  });
  const { events, defaultBreakPatternData, defaultFormErrors } = Constants;
  const { channelId, channelName } = channelInfo;
  const { contentLevel } = selectedRow;
  const [nameIsLoading, setNameIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState(defaultFormErrors);
  const [patternData, setPatternData] = useState({
    id: selectedRow.id ?? null,
    versionName: selectedRow.version ?? '',
    status: selectedRow.status === 'Active',
    default: selectedRow.default,
    breakChannelTypes: [{ ...defaultBreakPatternData, id: crypto.randomUUID() }],
    contentLevel: {
      eventType: contentLevel.eventType,
      eventName: contentLevel.eventName,
      eventDurationFrom: contentLevel.eventDurationFrom,
      eventDurationTo: contentLevel.eventDurationTo,
      timeOfDayFrom: contentLevel.timeOfDayFrom
        ? moment(contentLevel.timeOfDayFrom, 'HH:mm:ss').toDate()
        : null,
      timeOfDayTo: contentLevel.timeOfDayTo
        ? moment(contentLevel.timeOfDayTo, 'HH:mm:ss').toDate()
        : null,
      periodFrom: contentLevel.periodFrom,
      periodTo: contentLevel.periodTo,
    },
  });
  const [breakTypeEditor, setBreakTypeEditor] = useState({
    show: false,
    mode: null,
    data: null,
  });
  const [isFormSaving, setIsFormSaving] = useState(false);

  const [eventNameList, setEventNameList] = useState([]);

  const [sortingResult, setSortingResult] = useState({
    ascending: true,
  });

  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(isDirtyForm);

  // Switch properties
  const activeSwitchProps = React.useMemo(
    () => ({
      label: 'Active',
      labelStyle: {
        fontWeight: 'bold',
        color: '#6D6D73',
      },
      text: ['No', 'Yes'],
      active: patternData.status,
      disabled: patternData.breakChannelTypes.length === 0 || patternData.default,
      onSwitch: (e) => {
        setPatternData((prev) => ({ ...prev, status: e }));
        setFormErrors(defaultFormErrors);
        setDirtyForm(true);
      },
      blurredOnInactive: false,
    }),
    [patternData.breakChannelTypes, patternData.status, patternData.default],
  );

  const defaultSwitchProps = React.useMemo(
    () => ({
      label: 'Default',
      labelStyle: {
        fontWeight: 'bold',
        color: '#6D6D73',
      },
      text: ['No', 'Yes'],
      active: patternData.default ?? selectedRow?.default === 'Yes',
      onSwitch: (e) => {
        if (e) {
          setPatternData((prev) => ({
            ...prev,
            default: e,
            status: e,
            contentLevel: {
              eventType: null,
              eventName: null,
              eventDurationFrom: null,
              eventDurationTo: null,
              timeOfDayFrom: null,
              timeOfDayTo: null,
              periodFrom: null,
              periodTo: null,
            },
          }));
          setEventNameList([]);
        } else {
          setPatternData((prev) => ({ ...prev, default: e }));
          if (patternData.breakChannelTypes.length === 0)
            setPatternData((prev) => ({ ...prev, status: e }));
        }
        setDirtyForm(true);
        setFormErrors(defaultFormErrors);
      },
      blurredOnInactive: false,
    }),
    [patternData.breakChannelTypes],
  );

  const fetchChannelTemplate = (templateId) => {
    GetBreakChannelTypeAPI({
      queryParams: { breakChannelTemplateId: templateId },
      onSuccess: (res) => {
        setPatternData((prev) => ({ ...prev, breakChannelTypes: res }));
      },
    });
  };

  const changeImportModal = (attribute, value) => {
    setUploadState((prevState) => ({
      ...prevState,
      [attribute]: value,
    }));
  };

  const handeImportFile = () => {
    if (patternData.breakChannelTypes.length) {
      setUploadState({
        ...uploadState,
        modalComponent: importModalComponents.confirm,
      });
    } else {
      confirmImportFile();
    }
  };

  const confirmImportFile = async () => {
    setUploadState({
      ...uploadState,
      modalComponent: importModalComponents.loading,
    });

    const res = await onImport(uploadState.selectedFile);
    if (res.status === 200) {
      setDirtyForm(true);
      const importedData = res.data.items.map((value, index) => ({
        id: index,
        type: value.type,
        slotPosition: value.slotPosition,
        optional: value.optional === 'Yes',
        priority: value.priority ? Number.parseInt(value.priority, 10) : null,
        selection: value.selection,
        variable: value.variable === 'Yes',
        externalID: value.externalID,
        eventName: value.eventName,
        position: value.position ? Number.parseInt(value.position, 10) : null,
        assetID: value.assetID ? Number.parseInt(value.assetID, 10) : null,
        imported: value.imported,
        result: value.result,
      }));

      setUploadState((prevState) => ({
        ...prevState,
        resultList: importedData,
        modalComponent: importModalComponents.success,
        errorMessage: null,
      }));
      setPatternData((prev) => ({
        ...prev,
        breakChannelTypes: importedData.filter((item) => item.imported),
      }));
    } else {
      setUploadState((prevState) => ({
        ...prevState,
        modalComponent: importModalComponents.import,
        resultList: [],
        errorMessage:
          'Invalid file format. Please use files in the suggested format above (see sample template)',
      }));
    }
  };

  const handleUploadModal = () => {
    setUploadState({
      errorMessage: '',
      resultList: [],
      modalComponent: importModalComponents.import,
      show: true,
    });
  };

  const handleExportSampleTemplate = () => {
    const excelData = sampleUploadFile;
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const fileName = `break-pattern-import-template.xlsx`;
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = { Sheets: { errors: ws }, SheetNames: ['errors'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });
    saveAs(data, fileName);
  };

  const handleDownload = () => {
    if (patternData.breakChannelTypes.length > 0) {
      ExportBreakChannelTypeAPI({
        queryParams: {
          breakChannelTemplateId: patternData.id,
          payload: { sort: sortingResult },
        },
        onSuccess: (res) => {
          const { organisationName } = user;
          const exportedDateString = moment().format('DDMMYYYY');
          const fileName = `${organisationName}_op2mise_break_patterns_export_${channelName}_${exportedDateString}.xlsx`;
          const blob = new Blob([res], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          saveAs(blob, fileName);
        },
      });
    }
  };

  const handleAddBreakType = () => {
    // id here is used by the grid for properly rendering rows.
    const newRow = {
      ...Constants.defaultBreakPatternData,
      id: crypto.randomUUID(),
    };

    setBreakTypeEditor({
      show: true,
      mode: FORM_TYPE.CREATE,
      data: newRow,
    });
  };

  const handleEditBreakType = (params) => {
    setBreakTypeEditor({
      show: true,
      mode: FORM_TYPE.EDIT,
      data: params.data,
    });
  };

  const handleSaveBreakTypeEditor = (updatedData, formType) => {
    if (formType === FORM_TYPE.CREATE) {
      setPatternData((prevData) => ({
        ...prevData,
        breakChannelTypes: [...prevData.breakChannelTypes, updatedData],
      }));
    } else if (formType === FORM_TYPE.EDIT) {
      setPatternData((prevData) => ({
        ...prevData,
        breakChannelTypes: prevData.breakChannelTypes.map((row) =>
          row.id === updatedData.id ? updatedData : row,
        ),
      }));
    }

    setDirtyForm(true);
    setBreakTypeEditor({ show: false, data: null });
  };

  const saveBreakChannelTemplate = () => {
    const validateBreakPatternResult = validateBreakPattern(patternData);

    if (validateBreakPatternResult.hasErrors) {
      setFormErrors(validateBreakPatternResult);

      // cancel dirty form navigation
      setShowConfirmLeaveModal(false);
      cancelNavigation();
      return;
    }

    const constructedPatterns = patternData.breakChannelTypes
      .filter((pattern) => pattern != null)
      .map((pattern) => ({
        assetId: Number.isInteger(pattern.assetID) ? pattern.assetID : null,
        eventName: pattern.eventName ?? null,
        externalID: pattern.externalID ?? '',
        isVariableLength: pattern.variable ?? false,
        optional: pattern.optional ?? false,
        position: pattern.position ?? null,
        priority: pattern.priority ?? null,
        selection: pattern.selection ?? '',
        slotPosition: pattern.slotPosition ?? null,
        type: pattern.type ?? '',
      }));

    const {
      eventType,
      eventName,
      eventDurationFrom,
      eventDurationTo,
      timeOfDayFrom,
      timeOfDayTo,
      periodFrom,
      periodTo,
    } = patternData.contentLevel;

    //Construct payload data
    let payload = {
      versionName: patternData.versionName,
      active: patternData.status,
      default: patternData.default,
      contentLevel: {
        eventType,
        eventName,
        assedID: null,
        eventDurationFrom,
        eventDurationTo,
        timeOfDayFrom: timeOfDayFrom ? moment(timeOfDayFrom).format('HH:mm:ss') : null,
        timeOfDayTo: timeOfDayTo ? moment(timeOfDayTo).format('HH:mm:ss') : null,
        periodFrom: periodFrom ? moment(periodFrom).format('YYYY-MM-DDTHH:mm:ss') : null,
        periodTo: periodTo ? moment(periodTo).format('YYYY-MM-DDTHH:mm:ss') : null,
      },
      breakChannelTypes: constructedPatterns,
      id: mode === 'new' ? 0 : patternData.id,
    };

    setFormErrors(defaultFormErrors);
    SaveBreakChannelTemplateAPI({
      queryParams: { breakChannelTemplateId: channelId, payload: payload },
      onSuccess: (res) => {
        setPatternData((prev) => ({ ...prev, id: res }));
        fetchChannelTemplate(res);
        setMode('edit');
        success('Successfully saved!');
        setDirtyForm(false);
        setShowConfirmLeaveModal(false);

        // Dirty form navigation handlers
        if (showConfirmLeaveModal) onClose('view');
        if (showPrompt) confirmNavigation();
      },
      setLoader: setIsFormSaving,
    });
  };

  const fetchEventListTypes = (type) => {
    const types = {
      'Title Group': 'titlegroup',
      Series: 'series',
      Season: 'season',
      Program: 'program',
    };

    setNameIsLoading(true);
    setEventNameList([]);

    GetBreakChannelEventListAPI({
      queryParams: {
        channelId: channelInfo.channelId,
        type: types[type],
      },
      onSuccess: (res) => {
        let list = res.eventList;
        if (list.length) {
          list[list.length + 1] = { id: list.length + 1, name: 'None' };
        }
        setEventNameList(list);
        setNameIsLoading(false);
      },
    });
  };

  const handleInputChange = (event, type, field) => {
    let value;
    if (type === 'timeOfDay') value = event;
    else if (type === 'period') value = event.value;
    else value = event.target.value;

    const isStart = field === 'start';
    setDirtyForm(true);
    setFormErrors(defaultFormErrors);

    switch (type) {
      case 'patternName':
        setPatternData((prev) => ({
          ...prev,
          versionName: value,
        }));
        break;
      case 'duration':
        setPatternData((prev) => ({
          ...prev,
          contentLevel: {
            ...prev.contentLevel,
            eventDurationFrom: isStart ? value : prev.contentLevel.eventDurationFrom,
            eventDurationTo: !isStart ? value : prev.contentLevel.eventDurationTo,
          },
        }));
        break;
      case 'timeOfDay':
        setPatternData((prev) => ({
          ...prev,
          contentLevel: {
            ...prev.contentLevel,
            timeOfDayFrom: isStart ? value : prev.contentLevel.timeOfDayFrom,
            timeOfDayTo: !isStart ? value : prev.contentLevel.timeOfDayTo,
          },
        }));
        break;
      case 'period':
        setPatternData((prev) => ({
          ...prev,
          contentLevel: {
            ...prev.contentLevel,
            periodFrom: isStart ? value : prev.contentLevel.periodFrom,
            periodTo: !isStart ? value : prev.contentLevel.periodTo,
          },
        }));
        break;
    }
  };

  const handleDiscardChanges = () => {
    if (showPrompt) confirmNavigation();

    if (isDirtyForm) {
      setShowConfirmLeaveModal(false);
      onClose('view');
    }

    setDirtyForm(false);
  };

  const handleHideDirtyFormModals = () => {
    setShowConfirmLeaveModal(false);
    cancelNavigation();
  };

  useEffect(() => {
    // Fetch channel template if in edit mode
    if (mode === 'edit' && patternData.id) {
      fetchChannelTemplate(patternData.id);
    }

    // Initialize event name list on mount if an event type is already selected (edit mode)
    if (patternData.contentLevel.eventType) {
      fetchEventListTypes(patternData.contentLevel.eventType);
    }
  }, []);

  return (
    <div className="break-editor-wrapper">
      <div className="break-editor-form-fields">
        <div className="break-editor-form-grid">
          <div className="break-editor-field">
            <label>Pattern Name *</label>
            <div style={{ width: '70%' }}>
              <Input
                name="versionName"
                type="text"
                placeholder="Enter pattern name"
                value={patternData.versionName}
                maxLength={99}
                onChange={(e) => {
                  handleInputChange(e, 'patternName');
                }}
              />
              <BreakPatternErrorMessage message={formErrors.versionName} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Switch {...activeSwitchProps} />
            <Switch {...defaultSwitchProps} />
          </div>
        </div>
        <label>Content Level</label>
        <div className="break-editor-form-grid">
          <div className="left-fields">
            <div className="break-editor-field">
              <label>Type *</label>
              <div>
                <OpSelect
                  allowFiltering={true}
                  dataSource={events}
                  fields={{ text: 'name', value: 'id' }}
                  enabled={!patternData.default}
                  filterBarPlaceholder="Search"
                  width="26.5vw"
                  onChange={(e) => {
                    const args = e.target.itemData;

                    fetchEventListTypes(args.name);
                    setPatternData((prev) => ({
                      ...prev,
                      contentLevel: {
                        ...prev.contentLevel,
                        eventType: args.name,
                        eventName: null,
                      },
                    }));
                    setDirtyForm(true);
                    setFormErrors(defaultFormErrors);
                  }}
                  placeholder="Please select a type"
                  value={
                    patternData.contentLevel.eventType
                      ? events.find((item) => item.name === patternData.contentLevel.eventType)?.id
                      : null
                  }
                />
                <BreakPatternErrorMessage message={formErrors.contentLevel.eventType} />
              </div>
            </div>
            <div className="break-editor-field">
              <label>Name *</label>
              <div>
                <OpSelect
                  allowFiltering={true}
                  dataSource={
                    Array.isArray(eventNameList) && !nameIsLoading
                      ? eventNameList.filter(
                          (item) => item && typeof item === 'object' && item.id && item.name,
                        )
                      : []
                  }
                  fields={eventNameList && { text: 'name', value: 'id' }}
                  enabled={(!patternData.default && (patternData.contentLevel.eventType !== 'None'))}
                  filterBarPlaceholder="Search"
                  width="26.5vw"
                  onChange={(e) => {
                    const args = e.target.itemData;
                    if (args.name !== 'None') {
                      setPatternData((prev) => ({
                        ...prev,
                        contentLevel: {
                          ...prev.contentLevel,
                          eventName: args.name,
                        },
                      }));
                    } else {
                      setPatternData((prev) => ({
                        ...prev,
                        contentLevel: { ...prev.contentLevel, eventName: null },
                      }));
                    }
                    setDirtyForm(true);
                    setFormErrors(defaultFormErrors);
                  }}
                  placeholder={
                    patternData.contentLevel.eventName
                      ? patternData.contentLevel.eventName
                      : 'Please select a name'
                  }
                  value={
                    patternData.contentLevel.eventName &&
                    Array.isArray(eventNameList) &&
                    !nameIsLoading
                      ? (eventNameList.find(
                          (item) => item.name === patternData.contentLevel.eventName,
                        )?.id ?? null)
                      : null
                  }
                />
                <BreakPatternErrorMessage message={formErrors.contentLevel.eventName} />
              </div>
            </div>
            <div>
              <div className="break-editor-field">
                <label>Duration</label>
                <div className="content-level-input dual-input">
                  <NumericTextbox
                    label="From"
                    min={1}
                    max={999}
                    onChange={(e) => {
                      handleInputChange(e, 'duration', 'start');
                    }}
                    showSpinButton={false}
                    format={'##'}
                    cssClass="e-op2mise-numeric-textbox"
                    placeholder="Min."
                    style={{
                      textAlign: 'left',
                      padding: '0 10px',
                    }}
                    value={patternData.contentLevel.eventDurationFrom}
                    enabled={!patternData.default}
                  />
                  <NumericTextbox
                    label="To"
                    min={1}
                    max={999}
                    onChange={(e) => {
                      handleInputChange(e, 'duration', 'end');
                    }}
                    showSpinButton={false}
                    format={'##'}
                    cssClass="e-op2mise-numeric-textbox"
                    placeholder="Min."
                    style={{ textAlign: 'left', padding: '0 10px' }}
                    value={patternData.contentLevel.eventDurationTo}
                    enabled={!patternData.default}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="right-fields">
            <div>
              <div className="break-editor-field">
                <label>Time of Day</label>
                <div className="content-level-input dual-input">
                  <OpTimeInput
                    label="From"
                    value={patternData.contentLevel.timeOfDayFrom}
                    onChange={(e) => handleInputChange(e, 'timeOfDay', 'start')}
                    width="full"
                    min={'00:00'}
                    max={'24:00'}
                    style={{ width: '100%' }}
                    direction="row"
                    enabled={!patternData.default}
                  />
                  <OpTimeInput
                    value={patternData.contentLevel.timeOfDayTo}
                    label="To"
                    onChange={(e) => handleInputChange(e, 'timeOfDay', 'end')}
                    width="full"
                    style={{ width: '100%' }}
                    direction="row"
                    min={'00:00'}
                    max={'24:00'}
                    enabled={!patternData.default}
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="break-editor-field">
                <label>Period</label>
                <div className="content-level-input dual-input">
                  <DatePicker
                    allowEdit={false}
                    cssClass="view-schedules-date-picker"
                    format={'dd/MM/yyyy'}
                    label="From"
                    onChange={(args) => {
                      handleInputChange(args, 'period', 'start');
                    }}
                    showClearButton={false}
                    value={patternData.contentLevel.periodFrom}
                    enabled={!patternData.default}
                  />
                  <DatePicker
                    allowEdit={false}
                    cssClass="view-schedules-date-picker"
                    format={'dd/MM/yyyy'}
                    label="To"
                    onChange={(args) => {
                      handleInputChange(args, 'period', 'end');
                    }}
                    showClearButton={false}
                    value={patternData.contentLevel.periodTo}
                    enabled={!patternData.default}
                  />
                </div>
              </div>
            </div>
            <div className="break-editor-table-action-buttons">
              <img
                src={uploadFileIcon}
                alt="Import pattern icon"
                title="Import pattern"
                onClick={handleUploadModal}
                style={{
                  cursor: 'pointer',
                }}
              />
              <img
                src={mode === 'new' ? disabledDownloadFile : downloadFileIcon}
                alt={mode === 'new' ? 'Disabled export pattern icon' : 'Export pattern icon'}
                title="Export pattern"
                onClick={handleDownload}
                style={{
                  cursor: mode === 'new' ? 'not-allowed' : 'pointer',
                  height: mode === 'new' ? '28px' : undefined,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <BreakPatternEditorTable
        handleAddBreakType={handleAddBreakType}
        handleEditBreakType={handleEditBreakType}
        rowData={patternData.breakChannelTypes}
        setDirtyForm={setDirtyForm}
        setSortingResult={setSortingResult}
        setPatternData={setPatternData}
        breakChannelTableError={formErrors.breakChannelTableError}
      />

      <div className="break-editor-footer">
        <Button
          text="Close"
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: '#6d6d73',
          }}
          onClick={() => {
            if (isDirtyForm) setShowConfirmLeaveModal(true);
            else onClose('view');
          }}
        />
        <Button text="Save" onClick={saveBreakChannelTemplate} disabled={isFormSaving} />
      </div>
      <ImportFileModalComponent
        closeModal={() => {
          setUploadState({ ...uploadState, show: false });
        }}
        selectedFile={uploadState.selectedFile}
        setSelectedFile={(e) => changeImportModal('selectedFile', e)}
        onImportFile={handeImportFile}
        errorMessage={uploadState.errorMessage}
        showModal={uploadState.show}
        onExportSampleTemplate={handleExportSampleTemplate}
        modalComponent={uploadState.modalComponent}
        onProceed={confirmImportFile}
        resultList={uploadState.resultList}
      />

      {breakTypeEditor.show && (
        <BreakTypeEditor
          data={breakTypeEditor.data}
          channelInfo={channelInfo}
          mode={breakTypeEditor.mode}
          onClose={() => setBreakTypeEditor({ show: false, data: null })}
          onSave={handleSaveBreakTypeEditor}
        />
      )}

      <ConfirmLeaveModal
        show={showPrompt || showConfirmLeaveModal}
        onHide={handleHideDirtyFormModals}
        onSave={saveBreakChannelTemplate}
        onDiscard={handleDiscardChanges}
      />
    </div>
  );
}

export default BreakPatternEditor;
